import React, { useState, useEffect, useRef, useMemo } from 'react'
import * as XLSX from 'xlsx'
import { toast } from 'react-toastify'
import { apiLabs, apiSantoLog } from '../../../Services/api'
import Dashboard from '../../../Components/Dashboard'
import { store } from '../../../store'
import history from '../../../Services/history'
import Denied from '../../../Components/Denied'
import { formatBeepShort } from '../_shared/datetime'
import {
  GlobalOrdersStyles, STATUS_LABEL,
  PageWrap, Topbar, Body,
  StatsRow, StatCard,
  Toolbar, Search,
  ImportBtn, BulkActions, BulkBtn,
  TableWrap, Table, THead, HRow, Row,
  CheckCell, NfCell, MonoCell, VolumesCell, StatusBadge,
  Actions, IconBtn, ReentregaBtn, FormLabel, FormInput, FormSelect, Empty, Loading,
  ExpandToggle, VolumesAccordion, VolumeGrid, VolumeChip, AccordionItem,
  ModalOverlay, ImportBox, DropZone, FileInfo, ImportProgress,
  OccBox, OccSummary, OccSummaryCard, OccList, OccItem,
  StatusModalBox, StatusOptions, StatusOption,
  ModalActions, ModalBtn,
  ConfirmBox, ConfirmIcon, ConfirmTitle, ConfirmMessage,
  ColHead, ColLabel, ColFilterBtn, ColPopover,
  PopInput, PopDateRow, PopCheckList, PopActions, PopBtn,
  PaginationBar, PageBtn, PerPageSelect,
  FilterToggleBtn, DrawerBackdrop, Drawer, DrawerHeader,
  DrawerBody, DrawerField, DrawerInput, DrawerDateRow, DrawerChips, DrawerChip,
  DrawerFooter,
} from './style'

// ─── ÍCONES INLINE (outline) ──────────────────────────────────────────────────
const SearchIcon = (
  <svg viewBox="0 0 24 24"><circle cx="11" cy="11" r="7" /><path d="M21 21l-4.3-4.3" /></svg>
)
const FilterIcon = (
  <svg viewBox="0 0 24 24"><path d="M3 5h18M6 12h12M10 19h4" /></svg>
)

// ─── HELPERS ──────────────────────────────────────────────────────────────────
const STATUS_OPTIONS = [
  { id: 'aguardando_recebimento', label: 'Aguardando Recebimento', hint: 'Pedido importado, nenhum volume bipado ainda' },
  { id: 'coletado_parcial',       label: 'Coletado Parcial',       hint: 'Alguns volumes coletados, outros pendentes' },
  { id: 'coletado',               label: 'Coletado',               hint: 'Todos os volumes conferidos, pronto pra embarcar' },
  { id: 'embarcado',              label: 'Embarcado',              hint: 'Pedido despachado para o motorista' },
  { id: 'entregue',               label: 'Entregue',                hint: 'Pedido entregue ao destinatário' },
  { id: 'nao_embarcado',          label: 'Não Embarcado',          hint: 'Devolvido / retornado — não foi embarcado' },
]

const PER_PAGE_OPTIONS = [10, 50, 100, 200]
const BACKEND_PER_PAGE = 1000

function todayYMD() {
  const d = new Date()
  const y = d.getFullYear()
  const m = String(d.getMonth() + 1).padStart(2, '0')
  const day = String(d.getDate()).padStart(2, '0')
  return `${y}-${m}-${day}`
}

function emptyServerFilters() {
  return {
    status: new Set(),
    driver_name: new Set(),        // multi-select: Set<string> (drivers ou '__none__' pra "sem motorista")
    driver_id: '',                 // UUID match exato (uso avançado via drawer)
    without_driver: false,         // ⭐ flag — pedidos sem motorista (anula driver_id/driver_name)
    imported_at_from: '',
    imported_at_to: '',
    updated_at_from: '',           // ⭐ janela por data de alteração
    updated_at_to: '',
    recipient_document: '',
    recipient_name: '',
    email: '',
    route: '',
    q: '',
  }
}
function initialServerFilters() {
  // Default: importados hoje
  return { ...emptyServerFilters(), imported_at_from: todayYMD(), imported_at_to: todayYMD() }
}

function isFilterActive(value) {
  if (value == null) return false
  if (value instanceof Set) return value.size > 0
  if (typeof value === 'object') return !!(value.from || value.to)
  return String(value).trim() !== ''
}
function countActiveFilters(filters) {
  let n = 0
  for (const v of Object.values(filters)) if (isFilterActive(v)) n++
  return n
}

function fmtKg(n) {
  if (n === null || n === undefined || n === '') return '—'
  const num = Number(n)
  if (!Number.isFinite(num)) return '—'
  return num.toLocaleString('pt-BR', { minimumFractionDigits: 1, maximumFractionDigits: 3 })
}
function bytesToMB(b) {
  if (!b) return '0 KB'
  if (b < 1024) return b + ' B'
  if (b < 1024 * 1024) return (b / 1024).toFixed(1) + ' KB'
  return (b / 1024 / 1024).toFixed(2) + ' MB'
}
function fmtDate(d) {
  if (!d) return '—'
  const dt = new Date(d)
  if (Number.isNaN(dt.getTime())) return '—'
  return dt.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', year: '2-digit' })
    + ' ' + dt.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })
}

// Mapeia uma linha da planilha (.xlsx) → payload do POST /order/bulk
function rowToOrderPayload(row) {
  const get = (...keys) => {
    for (const k of keys) {
      if (row[k] !== null && row[k] !== undefined && row[k] !== '') return row[k]
    }
    return null
  }
  const cep = String(get('CEP') || '').replace(/\D/g, '')
  return {
    order_number:     String(get('Número', 'numero', 'NF') || '').trim(),
    recipient_name:   get('Destinatário', 'destinatario'),
    recipient_phone:  get('Telefone/Celular', 'telefone', 'celular'),
    email:            get('E-mail', 'email'),
    street:           get('Logradouro'),
    complement:       get('Complemento'),
    neighborhood:     get('Bairro'),
    city:             get('Cidade'),
    state_uf:         get('UF'),
    cep:              cep || null,
    driver_raw:       get('Motorista'),
    route:            get('Rota'),
    sequence_order:   Number(get('Sequência', 'sequencia')) || null,
    weight_kg:        Number(get('Peso (KG)', 'peso')) || 0,
    volumes_count:    Number(get('Quantidade de Volumes', 'volumes')) || 1,
    situation:        get('Situação', 'situacao'),
    classification:   get('Classificação', 'classificacao'),
    forecast_start:   get('Previsão Início - Data / Hora') || null,
    forecast_end:     get('Previsão Fim - Data / Hora') || null,
    observation:      get('Observação', 'observacao'),
    tags:             get('Tags'),
    recipient_signer: get('Recebedor'),
  }
}

// Mapeia a resposta do backend pro formato interno
function apiToLocal(o) {
  return {
    id: o.id,
    order_number: o.order_number || '',
    status: o.status || 'aguardando_recebimento',
    recipient_name: o.recipient_name || '',
    recipient_document: o.recipient_document || '',
    email: o.email || '',
    street: o.street || '',
    complement: o.complement || '',
    neighborhood: o.neighborhood || '',
    city: o.city || '',
    state_uf: o.state_uf || '',
    cep: o.cep || '',
    driver_name: o.driver_name || '',
    driver_phone: o.driver_phone || '',
    driver_id: o.driver_id || null,
    route: o.route || '',
    sequence_order: o.sequence_order ?? null,
    weight_kg: Number(o.weight_kg) || 0,
    volumes_count: Number(o.volumes_count) || 1,
    volumes_scanned_count: Number(o.volumes_scanned_count) || 0,
    situation: o.situation || '',
    classification: o.classification || '',
    embarked_at: o.embarked_at || null,
    imported_at: o.imported_at || o.createdAt || o.created_at || null,
    createdAt: o.createdAt || o.created_at || null,
    volumes: Array.isArray(o.volumes) ? o.volumes : [],
    has_reentrega: o.has_reentrega ?? false,
  }
}

// Converte serverFilters em query params do backend
function buildParams(f, page = 1) {
  const p = { page, perPage: BACKEND_PER_PAGE, include_volumes: true }
  if (f.status?.size) p.status = [...f.status].join(',')

  // without_driver tem precedência absoluta (anula driver_id/driver_name no backend).
  // 2 caminhos pra ativar:
  //   a) drawer marca o checkbox "apenas sem motorista" → without_driver=true
  //   b) coluna multi-select tem SÓ '__none__' selecionado → mais eficiente
  //      enviar pro backend ao invés de filtrar client-side.
  let driverSet = f.driver_name instanceof Set ? f.driver_name : new Set()
  const hasNoneSelected = driverSet.has('__none__')
  const realDrivers = [...driverSet].filter(d => d !== '__none__')
  const onlyNoneInColumn = hasNoneSelected && realDrivers.length === 0

  if (f.without_driver || onlyNoneInColumn) {
    p.without_driver = true
  } else {
    // driver_id (UUID, exato) — só envia se NÃO houver without_driver
    if (f.driver_id) p.driver_id = f.driver_id
    // driver_name CSV — só os reais (não '__none__'); se houver __none__ + outros,
    // mantém __none__ pra filtro client-side complementar
    if (realDrivers.length) p.driver_name = realDrivers.join(',')
  }

  if (f.imported_at_from) p.imported_at_from = `${f.imported_at_from}T00:00:00Z`
  if (f.imported_at_to)   p.imported_at_to   = `${f.imported_at_to}T23:59:59Z`
  if (f.updated_at_from)  p.updated_at_from  = `${f.updated_at_from}T00:00:00Z`
  if (f.updated_at_to)    p.updated_at_to    = `${f.updated_at_to}T23:59:59Z`
  if (f.recipient_document) p.recipient_document = f.recipient_document.replace(/\D/g, '')
  if (f.recipient_name) p.recipient_name = f.recipient_name
  if (f.email) p.email = f.email
  if (f.route) p.route = f.route
  if (f.q) p.q = f.q
  return p
}

// ─── ColumnHeader: label + filter button + popover ────────────────────────────
function ColumnHeader({
  label, type, filter, statusOptions, multiOptions, multiSearchable, open, onToggle, onClose, onChange, isActive,
}) {
  const [draft, setDraft] = useState(filter)
  const [multiSearch, setMultiSearch] = useState('')
  useEffect(() => { if (open) { setDraft(filter); setMultiSearch('') } }, [open]) // eslint-disable-line

  const popRef = useRef(null)
  useEffect(() => {
    if (!open) return
    function handleClick(e) {
      if (popRef.current && !popRef.current.contains(e.target)) onClose()
    }
    document.addEventListener('mousedown', handleClick)
    return () => document.removeEventListener('mousedown', handleClick)
  }, [open, onClose])

  function apply() {
    onChange(draft)
    onClose()
  }
  function reset() {
    const empty = type === 'date' ? { from: '', to: '' }
                : type === 'status' ? new Set()
                : type === 'multi'  ? new Set()
                : ''
    setDraft(empty)
    onChange(empty)
    onClose()
  }

  // Filtra opções do multi-select pela busca local
  const filteredMultiOptions = (multiOptions || []).filter(o => {
    if (!multiSearch) return true
    return String(o.label || '').toLowerCase().includes(multiSearch.toLowerCase())
  })

  return (
    <ColHead ref={popRef}>
      <ColLabel>{label}</ColLabel>
      <ColFilterBtn $active={isActive} onClick={onToggle} title="Filtrar coluna" type="button">
        {SearchIcon}
      </ColFilterBtn>
      {open && (
        <ColPopover onClick={e => e.stopPropagation()}>
          {type === 'text' && (
            <PopInput
              autoFocus
              placeholder="Buscar"
              value={draft || ''}
              onChange={e => { const v = e.target.value; setDraft(v) }}
              onKeyDown={e => { if (e.key === 'Enter') apply() }}
            />
          )}
          {type === 'date' && (
            <PopDateRow>
              <input
                type="date"
                value={draft?.from || ''}
                onChange={e => { const v = e.target.value; setDraft(d => ({ ...d, from: v })) }}
              />
              <span>→</span>
              <input
                type="date"
                value={draft?.to || ''}
                onChange={e => { const v = e.target.value; setDraft(d => ({ ...d, to: v })) }}
              />
            </PopDateRow>
          )}
          {type === 'status' && (
            <PopCheckList>
              {statusOptions.map(s => (
                <label key={s.id}>
                  <input
                    type="checkbox"
                    checked={draft?.has?.(s.id) || false}
                    onChange={e => {
                      const checked = e.target.checked
                      setDraft(prev => {
                        const next = new Set(prev || [])
                        if (checked) next.add(s.id); else next.delete(s.id)
                        return next
                      })
                    }}
                  />
                  {s.label}
                </label>
              ))}
            </PopCheckList>
          )}
          {type === 'multi' && (
            <>
              {multiSearchable && (
                <PopInput
                  autoFocus
                  placeholder="Buscar…"
                  value={multiSearch}
                  onChange={e => { const v = e.target.value; setMultiSearch(v) }}
                />
              )}
              <PopCheckList>
                {filteredMultiOptions.map(o => (
                  <label key={o.id}>
                    <input
                      type="checkbox"
                      checked={draft?.has?.(o.id) || false}
                      onChange={e => {
                        const checked = e.target.checked
                        setDraft(prev => {
                          const next = new Set(prev || [])
                          if (checked) next.add(o.id); else next.delete(o.id)
                          return next
                        })
                      }}
                    />
                    {o.label}
                  </label>
                ))}
                {filteredMultiOptions.length === 0 && (
                  <div style={{ fontSize: 11, color: '#5a6370', textAlign: 'center', padding: 8 }}>
                    Nenhuma opção encontrada
                  </div>
                )}
              </PopCheckList>
            </>
          )}
          <PopActions>
            <PopBtn onClick={reset} type="button">Resetar</PopBtn>
            <PopBtn $primary onClick={apply} type="button">Buscar</PopBtn>
          </PopActions>
        </ColPopover>
      )}
    </ColHead>
  )
}

// ─── COMPONENT ────────────────────────────────────────────────────────────────
export default function SantoLogOrders(props) {
  const { location: { idPage } } = props
  const [auth, setAuth] = useState(null)

  async function verifyAuth() {
    const { profile } = store.getState().user
    const res = await apiLabs.post('valid_key', { id: profile.id, menu_id: idPage })
    if (res.data.message === 'User not authorized') return setAuth('denied')
    if (res.data.message === 'User authorized') return setAuth('authorized')
    return history.push({ pathname: '/', state: { from: props.location } })
  }

  const [orders, setOrders] = useState([])
  const [totalsByStatus, setTotalsByStatus] = useState({
    aguardando_recebimento: 0, coletado_parcial: 0, coletado: 0,
    embarcado: 0, entregue: 0, nao_embarcado: 0,
  })
  const [loading, setLoading] = useState(false)
  const [saving, setSaving] = useState(false)

  // Busca client-side (rápida, sobre os pedidos já carregados)
  const [query, setQuery] = useState('')

  // Filtros que vão pro BACKEND como query params
  const [serverFilters, setServerFilters] = useState(initialServerFilters)
  const [openFilter, setOpenFilter] = useState(null)

  // Drawer de filtros
  const [drawerOpen, setDrawerOpen] = useState(false)
  const [drawerDraft, setDrawerDraft] = useState(serverFilters)

  // Paginação local
  const [pageSize, setPageSize] = useState(50)
  const [currentPage, setCurrentPage] = useState(1)

  // Accordion: id do pedido expandido (null = nenhum)
  const [expandedOrderId, setExpandedOrderId] = useState(null)

  // Seleção pra ações em lote
  const [selected, setSelected] = useState(() => new Set())

  // Modal de import
  const [importModal, setImportModal] = useState({ open: false, file: null, parsing: false, rows: [] })
  const importInputRef = useRef(null)
  const [dragOver, setDragOver] = useState(false)

  // Modal de ocorrências (resultado do bulk)
  const [occModal, setOccModal] = useState({ open: false, summary: null, occurrences: [] })

  // Modal de mudar status
  // statusModal aceita modo individual (order setado) OU bulk (bulkIds = array)
  const [statusModal, setStatusModal] = useState({ open: false, order: null, bulkIds: null, newStatus: null })

  // Modal de BLOQUEIO da coleta — o backend não deixa marcar "coletado" se
  // algum volume ainda não tem o primeiro bipe. Lista os rastreios que faltam.
  // items = [{ order_number, message, reason, missing_volumes: [{rastreio, volume}] }]
  const [blockModal, setBlockModal] = useState({ open: false, title: '', items: [] })
  const closeBlockModal = () => setBlockModal({ open: false, title: '', items: [] })

  // Motoristas (para o select de reentrega)
  const [drivers, setDrivers] = useState([])
  // Modal de reentrega (editar motorista + sequência)
  const [reModal, setReModal] = useState({ open: false, order: null, driverId: '', sequence: '', busy: false })

  // Confirm dialog (Promise-based)
  const [confirmDialog, setConfirmDialog] = useState({ open: false, title: '', message: '', confirmLabel: 'Confirmar', tone: 'warn' })
  const confirmResolveRef = useRef(null)
  function askConfirm({ title, message, confirmLabel = 'Confirmar', tone = 'warn' }) {
    return new Promise(resolve => {
      confirmResolveRef.current = resolve
      setConfirmDialog({ open: true, title, message, confirmLabel, tone })
    })
  }
  function closeConfirm(value) {
    setConfirmDialog(d => ({ ...d, open: false }))
    if (confirmResolveRef.current) {
      const r = confirmResolveRef.current
      confirmResolveRef.current = null
      r(Boolean(value))
    }
  }

  // ── INIT ──────────────────────────────────────────────────────────────────
  useEffect(() => { verifyAuth() }, []) // eslint-disable-line
  useEffect(() => {
    if (auth === 'authorized') { fetchOrders(serverFilters); fetchDrivers() }
  }, [auth]) // eslint-disable-line

  /**
   * Faz fetch paginado da rota /order com filtros server-side.
   * Carrega TODAS as páginas em paralelo antes de remontar a tabela.
   */
  async function fetchOrders(filtersToUse) {
    const f = filtersToUse || serverFilters
    setLoading(true)
    try {
      const first = await apiSantoLog.get('/order', { params: buildParams(f, 1) })
      const data1 = first.data || {}
      const totals = data1.totalsByStatus || {}
      const totalPages = data1.totalPages || data1.totalPage || 1

      let allRaw = data1.orders || data1.data || (Array.isArray(data1) ? data1 : [])

      if (totalPages > 1) {
        const reqs = []
        for (let p = 2; p <= totalPages; p++) {
          reqs.push(apiSantoLog.get('/order', { params: buildParams(f, p) }))
        }
        const rest = await Promise.all(reqs)
        for (const r of rest) {
          const list = r.data?.orders || r.data?.data || (Array.isArray(r.data) ? r.data : [])
          allRaw = allRaw.concat(list)
        }
      }

      setOrders(allRaw.map(apiToLocal))
      setTotalsByStatus({
        aguardando_recebimento: Number(totals.aguardando_recebimento) || 0,
        coletado_parcial:       Number(totals.coletado_parcial) || 0,
        coletado:               Number(totals.coletado) || 0,
        embarcado:              Number(totals.embarcado) || 0,
        entregue:               Number(totals.entregue) || 0,
        nao_embarcado:          Number(totals.nao_embarcado) || 0,
      })
      setCurrentPage(1)
    } catch (err) {
      console.error('GET /order failed:', err)
      toast.error('Erro ao carregar pedidos: ' + (err?.response?.data?.error || err?.response?.data?.message || err.message))
    } finally {
      setLoading(false)
    }
  }

  // ── FILTROS POR COLUNA ──────────────────────────────────────────────────────
  function applyColumnFilter(col, value) {
    // Mapeia a coluna pro campo do serverFilters
    // (NF da coluna usa o filtro `q` do backend, já que não há filtro exato por order_number)
    const fieldMap = {
      imported_at: '__date',          // tratado abaixo
      status:      'status',
      order_number:'q',
      driver_name: 'driver_name',
      email:       'email',
      recipient_document: 'recipient_document',
    }
    const field = fieldMap[col]
    if (!field) return
    const next = { ...serverFilters }
    if (field === '__date') {
      next.imported_at_from = value?.from || ''
      next.imported_at_to   = value?.to   || ''
    } else {
      next[field] = value
    }
    setServerFilters(next)
    fetchOrders(next)
  }

  function isColFilterActive(col) {
    if (col === 'imported_at') return !!(serverFilters.imported_at_from || serverFilters.imported_at_to)
    if (col === 'status') return serverFilters.status.size > 0
    if (col === 'order_number') return !!serverFilters.q
    if (col === 'driver_name') return serverFilters.driver_name?.size > 0
    return !!serverFilters[col]
  }
  function getColFilterValue(col) {
    if (col === 'imported_at') return { from: serverFilters.imported_at_from, to: serverFilters.imported_at_to }
    if (col === 'status') return serverFilters.status
    if (col === 'order_number') return serverFilters.q
    if (col === 'driver_name') return serverFilters.driver_name
    return serverFilters[col] || ''
  }

  // Opções únicas de motoristas (extraídas dos pedidos carregados) + opção "Sem motorista"
  const driverOptions = useMemo(() => {
    const set = new Set()
    let hasNone = false
    for (const o of orders) {
      if (o.driver_name) set.add(o.driver_name)
      else hasNone = true
    }
    const opts = [...set].sort().map(d => ({ id: d, label: d }))
    if (hasNone) opts.unshift({ id: '__none__', label: '⚠ Sem motorista' })
    return opts
  }, [orders])

  function toggleFilter(col) { setOpenFilter(prev => prev === col ? null : col) }
  function closeFilter() { setOpenFilter(null) }

  // ── DRAWER ────────────────────────────────────────────────────────────────
  function openDrawer() {
    setDrawerDraft({ ...serverFilters, status: new Set(serverFilters.status) })
    setDrawerOpen(true)
  }
  function closeDrawer() { setDrawerOpen(false) }
  function applyDrawer() {
    setServerFilters(drawerDraft)
    fetchOrders(drawerDraft)
    setDrawerOpen(false)
  }
  function clearAllFilters() {
    const empty = emptyServerFilters()
    setDrawerDraft(empty)
    setServerFilters(empty)
    fetchOrders(empty)
    setDrawerOpen(false)
  }
  function toggleDrawerStatus(id) {
    setDrawerDraft(d => {
      const next = new Set(d.status)
      if (next.has(id)) next.delete(id); else next.add(id)
      return { ...d, status: next }
    })
  }
  function setDrawerField(key, value) {
    setDrawerDraft(d => ({ ...d, [key]: value }))
  }

  // ── BUSCA CLIENT-SIDE (em cima dos dados já carregados) ────────────────────
  // "sem motorista" só precisa de filtro client-side quando vem MISTURADO
  // com nomes selecionados (caso __none__+outros). Quando é "só sem motorista",
  // o backend recebe without_driver=true e já filtra server-side.
  const wantSemMotoristaMixed = serverFilters.driver_name instanceof Set
    && serverFilters.driver_name.has('__none__')
    && serverFilters.driver_name.size > 1
  const q = query.trim().toLowerCase()
  const filtered = useMemo(() => {
    let list = orders
    // Caso "__none__ + outros drivers": server já trouxe pedidos com aqueles
    // drivers, mas faltam os SEM driver. Filtra localmente o conjunto completo
    // pra incluir ambos.
    if (wantSemMotoristaMixed) {
      const drivers = new Set([...serverFilters.driver_name].filter(d => d !== '__none__'))
      list = list.filter(o => !o.driver_name || drivers.has(o.driver_name))
    }
    if (!q) return list
    return list.filter(o => {
      const haystack = [
        o.order_number, o.recipient_name, o.recipient_document, o.driver_name,
        o.email, o.city, o.state_uf, o.cep, o.route,
      ].map(v => String(v || '').toLowerCase()).join(' ')
      return haystack.includes(q)
    })
  }, [orders, q, wantSemMotoristaMixed, serverFilters.driver_name])

  // Paginação local
  const totalPages = Math.max(1, Math.ceil(filtered.length / pageSize))
  const safePage = Math.min(currentPage, totalPages)
  const pageStart = (safePage - 1) * pageSize
  const pageEnd = Math.min(pageStart + pageSize, filtered.length)
  const pageRows = filtered.slice(pageStart, pageEnd)

  // ── SELECTION ─────────────────────────────────────────────────────────────
  function toggleSelect(id) {
    setSelected(prev => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id); else next.add(id)
      return next
    })
  }
  function selectAllOnPage() {
    setSelected(prev => {
      const next = new Set(prev)
      pageRows.forEach(o => next.add(o.id))
      return next
    })
  }
  function clearSelection() { setSelected(new Set()) }
  const selectedCount = selected.size
  const allPageSelected = pageRows.length > 0 && pageRows.every(o => selected.has(o.id))

  // ── IMPORT ────────────────────────────────────────────────────────────────
  function openImport() { setImportModal({ open: true, file: null, parsing: false, rows: [] }) }
  function closeImport() {
    if (importInputRef.current) importInputRef.current.value = ''
    setImportModal({ open: false, file: null, parsing: false, rows: [] })
  }
  function handleFile(file) {
    if (!file) return
    if (!file.name.match(/\.xlsx?$/i)) { toast.error('Arquivo inválido. Use .xlsx ou .xls'); return }
    setImportModal(m => ({ ...m, file, parsing: true, rows: [] }))
    const reader = new FileReader()
    reader.onload = (event) => {
      try {
        const wb = XLSX.read(event.target.result, { type: 'binary', cellDates: true })
        const sheetName = wb.SheetNames.find(n => n.toUpperCase() === 'BASE') || wb.SheetNames[0]
        const sheet = wb.Sheets[sheetName]
        const raw = XLSX.utils.sheet_to_json(sheet, { defval: null })
        const rows = raw.map(rowToOrderPayload).filter(r => r.order_number)
        if (!rows.length) {
          toast.error('Planilha vazia ou sem coluna "Número" reconhecida')
          setImportModal(m => ({ ...m, parsing: false }))
          return
        }
        setImportModal(m => ({ ...m, parsing: false, rows }))
      } catch (err) {
        console.error('parse xlsx failed:', err)
        toast.error('Erro ao processar arquivo: ' + err.message)
        setImportModal(m => ({ ...m, parsing: false }))
      }
    }
    reader.readAsBinaryString(file)
  }
  function handleDrop(e) {
    e.preventDefault()
    setDragOver(false)
    const file = e.dataTransfer.files?.[0]
    if (file) handleFile(file)
  }
  async function confirmImport() {
    if (!importModal.rows.length) return
    setSaving(true)
    try {
      const res = await apiSantoLog.post('/order/bulk', { orders: importModal.rows })
      const data = res.data || {}
      const summary = data.summary || {
        total: importModal.rows.length,
        created: (data.created || []).length,
        ignored: (data.ocorrencias || []).length,
      }
      const occurrences = data.ocorrencias || []
      closeImport()
      setOccModal({ open: true, summary, occurrences })
      fetchOrders(serverFilters)
    } catch (err) {
      console.error('POST /order/bulk failed:', err)
      toast.error('Erro ao importar: ' + (err?.response?.data?.error || err?.response?.data?.message || err.message))
    } finally { setSaving(false) }
  }

  // ── MOTORISTAS (para o select de reentrega) ──────────────────────────────
  async function fetchDrivers() {
    try {
      const res = await apiSantoLog.get('/driver')
      const list = Array.isArray(res.data) ? res.data : (res.data?.data || res.data?.drivers || [])
      setDrivers(list.map(d => ({ id: d.id, name: d.name || '', status: d.status !== false })))
    } catch (err) {
      console.error('GET /driver failed:', err)
    }
  }

  // ── STATUS (individual via /edit + bulk via /status/bulk) ────────────────
  function openStatusModal(order) {
    // Entregue: backend bloqueia qualquer mudança de status (422)
    if (order.status === 'entregue') {
      toast.warn('Pedido entregue — alteração de status bloqueada.')
      return
    }
    setStatusModal({ open: true, order, bulkIds: null, newStatus: null })
  }
  function openBulkStatusModal() {
    const ids = [...selected]
    if (!ids.length) return
    setStatusModal({ open: true, order: null, bulkIds: ids, newStatus: null })
  }
  function closeStatusModal() {
    setStatusModal({ open: false, order: null, bulkIds: null, newStatus: null })
  }

  async function confirmStatusChange() {
    const { order, bulkIds, newStatus } = statusModal
    if (!newStatus) { closeStatusModal(); return }
    if (!bulkIds && order && newStatus === order.status) { closeStatusModal(); return }

    const label = STATUS_LABEL[newStatus] || newStatus
    const effectMsg =
      newStatus === 'coletado' ? ' Os volumes serão vinculados ao box do motorista.'
      : newStatus === 'nao_embarcado' ? ' Os volumes sairão do box.'
      : newStatus === 'aguardando_recebimento' ? ' O pedido volta para aguardando e sai do box (reentrega).'
      : ''
    const ok = await askConfirm({
      title: bulkIds ? 'Alterar status em lote' : 'Alterar status',
      message: bulkIds
        ? `Aplicar o status "${label}" a ${bulkIds.length} pedido${bulkIds.length > 1 ? 's' : ''}?`
        : `Alterar a NF "${order?.order_number}" para "${label}"?${effectMsg}`,
      confirmLabel: 'Confirmar',
      tone: newStatus === 'nao_embarcado' ? 'danger' : 'warn',
    })
    if (!ok) return

    setSaving(true)
    try {
      if (bulkIds && bulkIds.length > 0) {
        // BULK — PUT /order/status/bulk (endpoint próprio, inalterado)
        const res = await apiSantoLog.put('/order/status/bulk', {
          order_ids: bulkIds, status: newStatus,
        })
        const updated = res.data?.updated || 0
        const ocorrencias = res.data?.ocorrencias || []
        if (updated > 0) toast.success(`${updated} pedido${updated > 1 ? 's' : ''} alterado${updated > 1 ? 's' : ''} para "${label}"`)
        clearSelection()
        closeStatusModal()
        fetchOrders(serverFilters)
        // Ocorrências (incluindo o bloqueio por falta do 1º bipe) vão para o
        // modal — só um toast não deixa claro QUAIS pedidos e o que falta bipar.
        if (ocorrencias.length) {
          console.warn('Ocorrências do bulk status:', ocorrencias)
          const blocked = ocorrencias.filter(o => o.reason === 'volumes_sem_primeiro_bipe').length
          setBlockModal({
            open: true,
            title: blocked > 0
              ? `${blocked} pedido${blocked > 1 ? 's' : ''} sem o primeiro bipe`
              : `${ocorrencias.length} pedido${ocorrencias.length > 1 ? 's' : ''} não atualizado${ocorrencias.length > 1 ? 's' : ''}`,
            items: ocorrencias,
          })
        }
        return
      }

      // INDIVIDUAL — PUT /order/:id/edit (coletado | nao_embarcado | aguardando→reentrega)
      const payload = newStatus === 'aguardando_recebimento'
        ? { reentrega: true }
        : { status: newStatus }
      const res = await apiSantoLog.put(`/order/${order.id}/edit`, payload)
      const updated = res.data?.order || res.data?.data || res.data || {}
      setOrders(prev => prev.map(o => o.id === order.id ? apiToLocal({ ...o, ...updated }) : o))
      closeStatusModal()
      toast.success(`Status alterado para "${label}"`)
    } catch (err) {
      console.error('PUT /order/:id/edit (status) failed:', err)
      const data = err?.response?.data || {}
      const missing = Array.isArray(data.missing_volumes) ? data.missing_volumes : []
      // 422 com missing_volumes = coleta bloqueada porque algum volume ainda
      // não tem o primeiro bipe. Mostra os rastreios que faltam bipar.
      if (missing.length > 0) {
        closeStatusModal()
        setBlockModal({
          open: true,
          title: 'Coleta bloqueada — volumes sem o primeiro bipe',
          items: [{
            order_number: order?.order_number,
            reason: 'volumes_sem_primeiro_bipe',
            message: data.error || data.message,
            missing_volumes: missing,
          }],
        })
      } else {
        toast.error('Erro ao alterar status: ' + (data.error || data.message || err.message))
      }
    } finally { setSaving(false) }
  }

  // ── REENTREGA (editar motorista + sequência e devolver p/ aguardando) ─────
  function openReentrega(order) {
    if (order.status === 'entregue') {
      toast.warn('Pedido entregue — reentrega bloqueada.')
      return
    }
    setReModal({
      open: true, order,
      driverId: order.driver_id || '',
      sequence: order.sequence_order ?? '',
      busy: false,
    })
  }
  function closeReentrega() {
    setReModal({ open: false, order: null, driverId: '', sequence: '', busy: false })
  }
  async function submitReentrega() {
    const { order, driverId, sequence } = reModal
    if (!order) return
    const ok = await askConfirm({
      title: 'Reentregar pedido',
      message: `Reentregar a NF "${order.order_number}"? O pedido volta para "Aguardando Recebimento", sai do box e será reatribuído ao motorista/sequência informados.`,
      confirmLabel: 'Reentregar',
      tone: 'warn',
    })
    if (!ok) return
    setReModal(m => ({ ...m, busy: true }))
    setSaving(true)
    try {
      const payload = {
        reentrega: true,
        driver_id: driverId || null,
        sequence_order: sequence === '' ? null : Number(sequence),
      }
      const res = await apiSantoLog.put(`/order/${order.id}/edit`, payload)
      const updated = res.data?.order || res.data?.data || res.data || {}
      setOrders(prev => prev.map(o => o.id === order.id ? apiToLocal({ ...o, ...updated }) : o))
      toast.success(`NF ${order.order_number} reenviada para entrega`)
      closeReentrega()
    } catch (err) {
      console.error('PUT /order/:id/edit (reentrega) failed:', err)
      toast.error('Erro ao reentregar: ' + (err?.response?.data?.error || err?.response?.data?.message || err.message))
      setReModal(m => ({ ...m, busy: false }))
    } finally { setSaving(false) }
  }

  // ── DELETE ────────────────────────────────────────────────────────────────
  async function deleteOrder(order) {
    const ok = await askConfirm({
      title: 'Remover pedido',
      message: `Tem certeza que deseja remover a NF "${order.order_number}"? Esta ação não pode ser desfeita.`,
      confirmLabel: 'Remover',
      tone: 'danger',
    })
    if (!ok) return
    setSaving(true)
    try {
      await apiSantoLog.delete(`/order/${order.id}`)
      setOrders(prev => prev.filter(o => o.id !== order.id))
      setSelected(prev => { const n = new Set(prev); n.delete(order.id); return n })
      toast.success('Pedido removido')
    } catch (err) {
      console.error('DELETE /order/:id failed:', err)
      toast.error('Erro ao remover: ' + (err?.response?.data?.error || err?.response?.data?.message || err.message))
    } finally { setSaving(false) }
  }

  // ── RENDER ────────────────────────────────────────────────────────────────
  const activeServerFilterCount = countActiveFilters(serverFilters)
  const showTableSkeleton = orders.length > 0 || loading // header sempre que existe contexto

  return (
    <Dashboard title="Pedidos">
      {auth === 'denied' && <Denied />}
      {auth === 'authorized' && (
        <>
          <GlobalOrdersStyles />
          <PageWrap>
            <Topbar>
              <h2>📦 Cadastro de Pedidos</h2>
              <span className="crumb">Importação e gestão dos pedidos do Monta Carga</span>
            </Topbar>

            <Body>
              {/* Stats Row — totais vêm de totalsByStatus do backend */}
              <StatsRow>
                <StatCard>
                  <div className="l">Total</div>
                  <div className="v">{
                    totalsByStatus.aguardando_recebimento +
                    totalsByStatus.coletado_parcial +
                    totalsByStatus.coletado +
                    totalsByStatus.embarcado +
                    totalsByStatus.entregue +
                    totalsByStatus.nao_embarcado
                  }</div>
                </StatCard>
                <StatCard $variant="aguardando_recebimento">
                  <div className="l">Ag. Recebimento</div>
                  <div className="v">{totalsByStatus.aguardando_recebimento}</div>
                </StatCard>
                <StatCard $variant="coletado_parcial">
                  <div className="l">Coletado Parcial</div>
                  <div className="v">{totalsByStatus.coletado_parcial}</div>
                </StatCard>
                <StatCard $variant="coletado">
                  <div className="l">Coletados</div>
                  <div className="v">{totalsByStatus.coletado}</div>
                </StatCard>
                <StatCard $variant="embarcado">
                  <div className="l">Embarcados</div>
                  <div className="v">{totalsByStatus.embarcado}</div>
                </StatCard>
                <StatCard $variant="entregue">
                  <div className="l">Entregues</div>
                  <div className="v">{totalsByStatus.entregue}</div>
                </StatCard>
                <StatCard $variant="nao_embarcado">
                  <div className="l">Não Embarcados</div>
                  <div className="v">{totalsByStatus.nao_embarcado}</div>
                </StatCard>
              </StatsRow>

              {/* Toolbar */}
              <Toolbar>
                <Search
                  placeholder="Buscar por NF, destinatário, motorista, CEP…"
                  value={query}
                  onChange={e => { const v = e.target.value; setQuery(v); setCurrentPage(1) }}
                />
                <FilterToggleBtn
                  type="button"
                  $active={activeServerFilterCount > 0}
                  onClick={openDrawer}
                >
                  {FilterIcon}
                  Filtros
                  {activeServerFilterCount > 0 && <span className="badge">{activeServerFilterCount}</span>}
                </FilterToggleBtn>
                <ImportBtn onClick={openImport} disabled={saving}>
                  + Importar pedidos
                </ImportBtn>
              </Toolbar>

              {/* Bulk actions (só aparece quando há seleção) */}
              {selectedCount > 0 && (
                <BulkActions>
                  <strong>{selectedCount}</strong> pedido{selectedCount > 1 ? 's' : ''} selecionado{selectedCount > 1 ? 's' : ''}
                  <span style={{ flex: 1 }} />
                  <BulkBtn onClick={openBulkStatusModal} disabled={saving}>
                    ⇅ Alterar status
                  </BulkBtn>
                  <BulkBtn className="ghost" onClick={clearSelection}>
                    ✕ Limpar seleção
                  </BulkBtn>
                </BulkActions>
              )}

              {/* Tabela */}
              <TableWrap>
                {/* Header SEMPRE visível — empty/loading renderizam DENTRO da Table. */}
                <Table>
                  <THead>
                    <HRow>
                        <div>
                          <input
                            type="checkbox"
                            checked={allPageSelected}
                            onChange={() => allPageSelected ? clearSelection() : selectAllOnPage()}
                          />
                        </div>
                        <div>
                          <ColumnHeader
                            label="Data Importação" type="date"
                            filter={getColFilterValue('imported_at')}
                            open={openFilter === 'imported_at'}
                            onToggle={() => toggleFilter('imported_at')}
                            onClose={closeFilter}
                            onChange={v => applyColumnFilter('imported_at', v)}
                            isActive={isColFilterActive('imported_at')}
                          />
                        </div>
                        <div>
                          <ColumnHeader
                            label="Status" type="status"
                            statusOptions={STATUS_OPTIONS}
                            filter={getColFilterValue('status')}
                            open={openFilter === 'status'}
                            onToggle={() => toggleFilter('status')}
                            onClose={closeFilter}
                            onChange={v => applyColumnFilter('status', v)}
                            isActive={isColFilterActive('status')}
                          />
                        </div>
                        <div>
                          <ColumnHeader
                            label="NF" type="text"
                            filter={getColFilterValue('order_number')}
                            open={openFilter === 'order_number'}
                            onToggle={() => toggleFilter('order_number')}
                            onClose={closeFilter}
                            onChange={v => applyColumnFilter('order_number', v)}
                            isActive={isColFilterActive('order_number')}
                          />
                        </div>
                        <div><ColLabel>Peso</ColLabel></div>
                        <div><ColLabel>Volumes</ColLabel></div>
                        <div>
                          <ColumnHeader
                            label="Motorista" type="multi"
                            multiOptions={driverOptions}
                            multiSearchable
                            filter={getColFilterValue('driver_name')}
                            open={openFilter === 'driver_name'}
                            onToggle={() => toggleFilter('driver_name')}
                            onClose={closeFilter}
                            onChange={v => applyColumnFilter('driver_name', v)}
                            isActive={isColFilterActive('driver_name')}
                          />
                        </div>
                        <div>
                          <ColumnHeader
                            label="Cliente E-mail" type="text"
                            filter={getColFilterValue('email')}
                            open={openFilter === 'email'}
                            onToggle={() => toggleFilter('email')}
                            onClose={closeFilter}
                            onChange={v => applyColumnFilter('email', v)}
                            isActive={isColFilterActive('email')}
                          />
                        </div>
                        <div>
                          <ColumnHeader
                            label="Cliente Documento" type="text"
                            filter={getColFilterValue('recipient_document')}
                            open={openFilter === 'recipient_document'}
                            onToggle={() => toggleFilter('recipient_document')}
                            onClose={closeFilter}
                            onChange={v => applyColumnFilter('recipient_document', v)}
                            isActive={isColFilterActive('recipient_document')}
                          />
                        </div>
                        <div style={{ textAlign: 'right' }}>Ações</div>
                      </HRow>
                    </THead>

                    {pageRows.map(o => {
                      const isSelected = selected.has(o.id)
                      const volumesComplete = o.volumes_scanned_count >= o.volumes_count
                      const isExpanded = expandedOrderId === o.id
                      const volumes = Array.isArray(o.volumes) ? o.volumes : []
                      const toggleExpand = () => setExpandedOrderId(prev => prev === o.id ? null : o.id)
                      return (
                        <AccordionItem key={o.id}>
                          <Row $selected={isSelected}>
                            <CheckCell onClick={e => e.stopPropagation()}>
                              <input
                                type="checkbox"
                                checked={isSelected}
                                onChange={() => toggleSelect(o.id)}
                              />
                            </CheckCell>
                            <div
                              style={{ fontFamily: "'Space Mono',monospace", fontSize: 11, color: '#8a95a3', cursor: 'pointer', gap: 6 }}
                              onClick={toggleExpand}
                            >
                              <ExpandToggle $open={isExpanded} type="button">▾</ExpandToggle>
                              {fmtDate(o.imported_at)}
                            </div>
                            <div onClick={toggleExpand} style={{ cursor: 'pointer' }}>
                              <StatusBadge $status={o.status}>
                                {STATUS_LABEL[o.status] || o.status}
                              </StatusBadge>
                            </div>
                            <NfCell onClick={toggleExpand} style={{ cursor: 'pointer' }}>
                              {o.sequence_order != null && (
                                <span
                                  title={`Sequência de entrega #${o.sequence_order}`}
                                  style={{
                                    marginRight: 6,
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    padding: '1px 6px',
                                    borderRadius: 4,
                                    fontSize: 10,
                                    fontWeight: 700,
                                    background: '#1e293b',
                                    color: '#94a3b8',
                                    border: '1px solid #334155',
                                    fontFamily: "'Space Mono', monospace",
                                    verticalAlign: 'middle',
                                  }}
                                >
                                  #{o.sequence_order}
                                </span>
                              )}
                              {o.order_number}
                              {o.has_reentrega && (
                                <span
                                  title="Pedido importado em reentrega"
                                  style={{
                                    marginLeft: 6,
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    padding: '1px 6px',
                                    borderRadius: 4,
                                    fontSize: 9,
                                    fontWeight: 700,
                                    letterSpacing: 0.3,
                                    background: '#fee2e2',
                                    color: '#b91c1c',
                                    border: '1px solid #fca5a5',
                                    verticalAlign: 'middle',
                                  }}
                                >
                                  ↩ REENTREGA
                                </span>
                              )}
                            </NfCell>
                            <MonoCell>
                              {fmtKg(o.weight_kg)}<span className="unit">kg</span>
                            </MonoCell>
                            <VolumesCell $complete={volumesComplete}>
                              {o.volumes_scanned_count}<span className="total">/{o.volumes_count}</span>
                            </VolumesCell>
                            <div style={{ fontSize: 12 }} title={o.driver_name}>{o.driver_name || '—'}</div>
                            <div style={{ fontSize: 12 }} title={o.email}>{o.email || '—'}</div>
                            <div style={{ fontSize: 12, fontFamily: "'Space Mono',monospace" }}>{o.recipient_document || '—'}</div>
                            <div>
                              <Actions>
                                <ReentregaBtn
                                  onClick={() => openReentrega(o)}
                                  disabled={saving || o.status === 'entregue'}
                                  title={o.status === 'entregue' ? 'Pedido entregue — reentrega bloqueada' : 'Reentregar (editar motorista/sequência)'}
                                >
                                  ↩ Reentregar
                                </ReentregaBtn>
                                <IconBtn $warn onClick={() => openStatusModal(o)} disabled={saving || o.status === 'entregue'} title="Alterar status">
                                  ⇅
                                </IconBtn>
                                <IconBtn $danger onClick={() => deleteOrder(o)} disabled={saving} title="Remover pedido">
                                  🗑
                                </IconBtn>
                              </Actions>
                            </div>
                          </Row>

                          {/* Painel accordion full-width abaixo da Row */}
                          {isExpanded && (
                            <VolumesAccordion>
                              <h5>Volumes do pedido ({volumes.length})</h5>
                              {volumes.length === 0 ? (
                                <div style={{ fontSize: 12, color: '#5a6370', textAlign: 'center', padding: 12 }}>
                                  Pedido sem volumes carregados.
                                </div>
                              ) : (
                                <VolumeGrid>
                                  {[...volumes]
                                    .sort((a, b) => (Number(a.volume || a.volume_number || 0) - Number(b.volume || b.volume_number || 0)))
                                    .map(v => (
                                      <VolumeChip key={v.id || v.rastreio} $status={v.status}>
                                        <span className="num">V{v.volume || v.volume_number || '?'}</span>
                                        <div className="body">
                                          <div className="rastreio">{v.rastreio || '—'}</div>
                                          <div className="meta" title={`${STATUS_LABEL[v.status] || v.status}${o.sequence_order != null ? ` · seq ${o.sequence_order}` : ''}${o.driver_name ? ` · ${o.driver_name}` : ''}`}>
                                            {STATUS_LABEL[v.status] || v.status}
                                            {o.sequence_order != null && ` · seq ${o.sequence_order}`}
                                            {o.driver_name && ` · 🚛 ${o.driver_name}`}
                                          </div>
                                          {v.first_scanned_at && (
                                            <div className="beep">🔵 1º beep {formatBeepShort(v.first_scanned_at)}</div>
                                          )}
                                        </div>
                                      </VolumeChip>
                                    ))}
                                </VolumeGrid>
                              )}
                            </VolumesAccordion>
                          )}
                        </AccordionItem>
                      )
                    })}

                  {/* Empty / Loading DENTRO da Table, ocupando todas as colunas
                      (gridColumn: 1/-1) abaixo do header sticky. */}
                  {loading && pageRows.length === 0 && (
                    <Empty style={{ borderRadius: 0, border: 'none', margin: 0, gridColumn: '1 / -1' }}>
                      Carregando pedidos…
                    </Empty>
                  )}
                  {!loading && pageRows.length === 0 && (
                    <Empty style={{ borderRadius: 0, border: 'none', margin: 0, gridColumn: '1 / -1' }}>
                      Nenhum pedido encontrado para os filtros atuais.<br />
                      <span style={{ fontSize: 11 }}>Ajuste os filtros ou clique em <strong>"+ Importar pedidos"</strong>.</span>
                    </Empty>
                  )}
                </Table>
              </TableWrap>

              {/* Paginação local */}
              {filtered.length > 0 && (
                <PaginationBar>
                  <span className="info">
                    {pageStart + 1}–{pageEnd} de {filtered.length} {filtered.length === 1 ? 'item' : 'itens'}
                    {filtered.length !== orders.length && ` (filtrado de ${orders.length})`}
                  </span>
                  <div className="nav">
                    <PageBtn onClick={() => setCurrentPage(1)} disabled={safePage === 1}>«</PageBtn>
                    <PageBtn onClick={() => setCurrentPage(p => Math.max(1, p - 1))} disabled={safePage === 1}>‹</PageBtn>
                    <PageBtn $active>{safePage}</PageBtn>
                    <span style={{ color: '#5a6370', fontSize: 11, padding: '0 4px' }}>/ {totalPages}</span>
                    <PageBtn onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))} disabled={safePage === totalPages}>›</PageBtn>
                    <PageBtn onClick={() => setCurrentPage(totalPages)} disabled={safePage === totalPages}>»</PageBtn>
                  </div>
                  <PerPageSelect
                    value={pageSize}
                    onChange={e => { const v = Number(e.target.value); setPageSize(v); setCurrentPage(1) }}
                  >
                    {PER_PAGE_OPTIONS.map(opt => (
                      <option key={opt} value={opt}>{opt} / página</option>
                    ))}
                  </PerPageSelect>
                </PaginationBar>
              )}
            </Body>
          </PageWrap>

          {/* ─── DRAWER DE FILTROS ─────────────────────────────────────── */}
          <DrawerBackdrop $visible={drawerOpen} onClick={closeDrawer} />
          <Drawer $visible={drawerOpen}>
            <DrawerHeader>
              <h3>Filtros</h3>
              <button type="button" className="close" onClick={closeDrawer} title="Fechar">✕</button>
            </DrawerHeader>

            <DrawerBody>
              <DrawerField>
                <span className="lbl">Busca textual</span>
                <DrawerInput
                  placeholder="NF, destinatário ou cidade"
                  value={drawerDraft.q}
                  onChange={e => { const v = e.target.value; setDrawerField('q', v) }}
                />
                <span className="hint">Aplica o filtro <code>q</code> do backend (LIKE em order_number, recipient_name e city).</span>
              </DrawerField>

              <DrawerField>
                <span className="lbl">Status</span>
                <DrawerChips>
                  {STATUS_OPTIONS.map(s => (
                    <DrawerChip
                      key={s.id}
                      type="button"
                      $active={drawerDraft.status.has(s.id)}
                      onClick={() => toggleDrawerStatus(s.id)}
                    >
                      {s.label}
                    </DrawerChip>
                  ))}
                </DrawerChips>
              </DrawerField>

              <DrawerField>
                <span className="lbl">Data Importação</span>
                <DrawerDateRow>
                  <input
                    type="date"
                    value={drawerDraft.imported_at_from}
                    onChange={e => { const v = e.target.value; setDrawerField('imported_at_from', v) }}
                  />
                  <span>→</span>
                  <input
                    type="date"
                    value={drawerDraft.imported_at_to}
                    onChange={e => { const v = e.target.value; setDrawerField('imported_at_to', v) }}
                  />
                </DrawerDateRow>
              </DrawerField>

              <DrawerField>
                <span className="lbl">Data Alteração ⭐</span>
                <DrawerDateRow>
                  <input
                    type="date"
                    value={drawerDraft.updated_at_from}
                    onChange={e => { const v = e.target.value; setDrawerField('updated_at_from', v) }}
                  />
                  <span>→</span>
                  <input
                    type="date"
                    value={drawerDraft.updated_at_to}
                    onChange={e => { const v = e.target.value; setDrawerField('updated_at_to', v) }}
                  />
                </DrawerDateRow>
                <span className="hint">Filtra pela última vez que o pedido foi alterado (status, dados etc).</span>
              </DrawerField>

              <DrawerField>
                <span className="lbl">Motorista (use o filtro da coluna pra multi-select)</span>
                <DrawerInput
                  placeholder='Nome exato. Aceita CSV: "Alex,João,Maria"'
                  value={drawerDraft.driver_name instanceof Set
                    ? [...drawerDraft.driver_name].filter(x => x !== '__none__').join(',')
                    : (drawerDraft.driver_name || '')}
                  onChange={e => {
                    const v = e.target.value
                    // converte CSV digitada em Set; mantém __none__ se já estava
                    const next = new Set(v.split(',').map(s => s.trim()).filter(Boolean))
                    if (drawerDraft.driver_name instanceof Set && drawerDraft.driver_name.has('__none__')) {
                      next.add('__none__')
                    }
                    setDrawerField('driver_name', next)
                  }}
                />
              </DrawerField>

              <DrawerField>
                <span className="lbl">Motorista — ID (UUID exato)</span>
                <DrawerInput
                  placeholder="ex: 7e21b3a8-..."
                  value={drawerDraft.driver_id || ''}
                  onChange={e => { const v = e.target.value; setDrawerField('driver_id', v) }}
                />
              </DrawerField>

              <DrawerField>
                <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontSize: 13, color: '#e8ecf0' }}>
                  <input
                    type="checkbox"
                    checked={!!drawerDraft.without_driver}
                    onChange={e => { const v = e.target.checked; setDrawerField('without_driver', v) }}
                  />
                  <span>⚠ <strong>Apenas pedidos sem motorista</strong> (anula filtros de motorista acima)</span>
                </label>
              </DrawerField>

              <DrawerField>
                <span className="lbl">Documento (CPF / CNPJ)</span>
                <DrawerInput
                  placeholder="Só dígitos, sem máscara"
                  value={drawerDraft.recipient_document}
                  onChange={e => { const v = e.target.value; setDrawerField('recipient_document', v) }}
                />
              </DrawerField>

              <DrawerField>
                <span className="lbl">Nome do Destinatário</span>
                <DrawerInput
                  placeholder="Busca parcial (LIKE)"
                  value={drawerDraft.recipient_name}
                  onChange={e => { const v = e.target.value; setDrawerField('recipient_name', v) }}
                />
              </DrawerField>

              <DrawerField>
                <span className="lbl">E-mail</span>
                <DrawerInput
                  placeholder="Busca parcial (LIKE)"
                  value={drawerDraft.email}
                  onChange={e => { const v = e.target.value; setDrawerField('email', v) }}
                />
              </DrawerField>

              <DrawerField>
                <span className="lbl">Rota</span>
                <DrawerInput
                  placeholder="Match exato"
                  value={drawerDraft.route}
                  onChange={e => { const v = e.target.value; setDrawerField('route', v) }}
                />
              </DrawerField>
            </DrawerBody>

            <DrawerFooter>
              <ModalBtn onClick={clearAllFilters} disabled={loading}>Limpar</ModalBtn>
              <ModalBtn $primary onClick={applyDrawer} disabled={loading}>
                {loading ? 'Buscando…' : 'Buscar'}
              </ModalBtn>
            </DrawerFooter>
          </Drawer>

          {/* ─── MODAL IMPORT ──────────────────────────────────────────── */}
          <ModalOverlay $visible={importModal.open}>
            <ImportBox>
              <h3>Importar pedidos</h3>
              <p>
                Selecione a planilha .xlsx (aba <strong>BASE</strong>) com os pedidos.
                O sistema vai ignorar pedidos já cadastrados e devolver as ocorrências.
              </p>

              <DropZone
                $drag={dragOver}
                $hasFile={!!importModal.file}
                onDragOver={e => { e.preventDefault(); setDragOver(true) }}
                onDragLeave={() => setDragOver(false)}
                onDrop={handleDrop}
                onClick={() => importInputRef.current?.click()}
              >
                <input
                  ref={importInputRef}
                  type="file"
                  accept=".xlsx,.xls"
                  onChange={e => { if (e.target.files?.[0]) handleFile(e.target.files[0]) }}
                />
                <div className="icon">📂</div>
                <h4>Arraste a planilha aqui</h4>
                <p>ou clique para selecionar (.xlsx, .xls)</p>
              </DropZone>

              {importModal.file && (
                <FileInfo>
                  <div className="meta">
                    <div className="name">{importModal.file.name}</div>
                    <div className="size">{bytesToMB(importModal.file.size)}</div>
                  </div>
                  {importModal.parsing && <span style={{ fontSize: 11, color: '#8a95a3' }}>Lendo…</span>}
                  {!importModal.parsing && importModal.rows.length > 0 && (
                    <span className="ok">✓ {importModal.rows.length} linhas</span>
                  )}
                </FileInfo>
              )}

              {saving && (
                <ImportProgress>
                  Enviando {importModal.rows.length} pedidos para o backend…
                </ImportProgress>
              )}

              <ModalActions>
                <ModalBtn onClick={closeImport} disabled={saving}>Cancelar</ModalBtn>
                <ModalBtn
                  $primary
                  onClick={confirmImport}
                  disabled={saving || importModal.parsing || importModal.rows.length === 0}
                >
                  {saving ? 'Importando…' : `Importar ${importModal.rows.length || ''} pedido${importModal.rows.length !== 1 ? 's' : ''}`}
                </ModalBtn>
              </ModalActions>
            </ImportBox>
          </ModalOverlay>

          {/* ─── MODAL OCORRÊNCIAS ─────────────────────────────────────── */}
          <ModalOverlay $visible={occModal.open}>
            <OccBox>
              <h3>Resultado da importação</h3>
              <OccSummary>
                <OccSummaryCard $variant="created">
                  <div className="l">Criados</div>
                  <div className="v" style={{ color: '#00e5a0' }}>{occModal.summary?.created ?? 0}</div>
                </OccSummaryCard>
                <OccSummaryCard $variant="ignored">
                  <div className="l">Ignorados</div>
                  <div className="v" style={{ color: '#f59e0b' }}>{occModal.summary?.ignored ?? 0}</div>
                </OccSummaryCard>
                <OccSummaryCard>
                  <div className="l">Total enviado</div>
                  <div className="v">{occModal.summary?.total ?? 0}</div>
                </OccSummaryCard>
              </OccSummary>

              {occModal.occurrences.length > 0 ? (
                <OccList>
                  {occModal.occurrences.map((occ, idx) => (
                    <OccItem key={idx} $reason={occ.reason}>
                      <span className="nf">{occ.order_number || '—'}</span>
                      <span className="msg">{occ.message || occ.reason}</span>
                      <span className="tag">{occ.reason}</span>
                    </OccItem>
                  ))}
                </OccList>
              ) : (
                <div style={{ padding: 20, textAlign: 'center', fontSize: 12, color: '#8a95a3' }}>
                  Nenhuma ocorrência. Todos os pedidos foram importados.
                </div>
              )}

              <ModalActions>
                <ModalBtn $primary onClick={() => setOccModal({ open: false, summary: null, occurrences: [] })}>
                  Fechar
                </ModalBtn>
              </ModalActions>
            </OccBox>
          </ModalOverlay>

          {/* ─── MODAL BLOQUEIO DA COLETA (volumes sem o 1º bipe) ─────────── */}
          <ModalOverlay $visible={blockModal.open}>
            <OccBox>
              <h3>⚠ {blockModal.title}</h3>
              <div style={{
                margin: '0 0 12px',
                padding: '10px 12px',
                background: 'rgba(245,158,11,.10)',
                border: '1px solid rgba(245,158,11,.35)',
                borderRadius: 8,
                fontSize: 12.5,
                color: '#e8ecf0',
                lineHeight: 1.5,
              }}>
                Só é possível coletar um pedido depois que <strong>todos os volumes
                tiverem o primeiro bipe registrado</strong>. Basta o volume ter sido
                bipado na chegada — não precisa ter passado pela conferência. Bipe os
                volumes listados abaixo e tente novamente.
              </div>

              <OccList>
                {blockModal.items.map((occ, idx) => {
                  const missing = Array.isArray(occ.missing_volumes) ? occ.missing_volumes : []
                  return (
                    <div key={occ.order_id || occ.order_number || idx} style={{ marginBottom: 8 }}>
                      <OccItem $reason={occ.reason}>
                        <span className="nf">{occ.order_number || '—'}</span>
                        <span className="msg">{occ.message || occ.reason}</span>
                        <span className="tag">{occ.reason}</span>
                      </OccItem>
                      {missing.length > 0 && (
                        <div style={{
                          display: 'flex',
                          flexWrap: 'wrap',
                          gap: 6,
                          padding: '8px 10px 2px',
                        }}>
                          {missing.map((v, i) => (
                            <span
                              key={v.id || v.rastreio || i}
                              title={`Volume ${v.volume ?? '?'} — sem o primeiro bipe`}
                              style={{
                                fontFamily: "'Space Mono', monospace",
                                fontSize: 11,
                                fontWeight: 700,
                                color: '#fbbf24',
                                background: 'rgba(245,158,11,.12)',
                                border: '1px dashed rgba(245,158,11,.45)',
                                borderRadius: 4,
                                padding: '4px 8px',
                              }}
                            >
                              {v.rastreio || '—'}
                              {v.volume != null && (
                                <span style={{ color: '#8a95a3', fontWeight: 400 }}> · V{v.volume}</span>
                              )}
                            </span>
                          ))}
                        </div>
                      )}
                    </div>
                  )
                })}
              </OccList>

              <ModalActions>
                <ModalBtn $primary onClick={closeBlockModal}>Entendi</ModalBtn>
              </ModalActions>
            </OccBox>
          </ModalOverlay>

          {/* ─── MODAL ALTERAR STATUS (individual + bulk) ───────────────── */}
          <ModalOverlay $visible={statusModal.open}>
            <StatusModalBox>
              <h3>{statusModal.bulkIds ? 'Alterar status em lote' : 'Alterar status'}</h3>
              {statusModal.bulkIds ? (
                <p>
                  <strong>{statusModal.bulkIds.length}</strong> pedido{statusModal.bulkIds.length > 1 ? 's' : ''} selecionado{statusModal.bulkIds.length > 1 ? 's' : ''}.
                  O backend valida cada transição — pedidos em status incompatível ficam como ocorrência (não falham os outros).
                </p>
              ) : (
                <p>
                  NF <strong>{statusModal.order?.order_number}</strong> — status atual:{' '}
                  <strong>{STATUS_LABEL[statusModal.order?.status] || statusModal.order?.status}</strong>
                </p>
              )}

              <StatusOptions>
                {(statusModal.bulkIds
                  ? STATUS_OPTIONS
                  : STATUS_OPTIONS.filter(s => s.id === 'coletado' || s.id === 'nao_embarcado')
                ).map(s => (
                  <StatusOption
                    key={s.id}
                    $checked={statusModal.newStatus === s.id}
                    $status={s.id}
                  >
                    <input
                      type="radio"
                      name="status"
                      checked={statusModal.newStatus === s.id}
                      onChange={() => setStatusModal(m => ({ ...m, newStatus: s.id }))}
                    />
                    <div>
                      <div className="lbl">{s.label}</div>
                      <div className="hint">{s.hint}</div>
                    </div>
                  </StatusOption>
                ))}
              </StatusOptions>

              <ModalActions>
                <ModalBtn onClick={closeStatusModal} disabled={saving}>Cancelar</ModalBtn>
                <ModalBtn
                  $primary
                  onClick={confirmStatusChange}
                  disabled={saving || !statusModal.newStatus
                    || (!statusModal.bulkIds && statusModal.newStatus === statusModal.order?.status)}
                >
                  {saving ? 'Salvando…' : (statusModal.bulkIds ? `Aplicar a ${statusModal.bulkIds.length} pedido${statusModal.bulkIds.length > 1 ? 's' : ''}` : 'Confirmar')}
                </ModalBtn>
              </ModalActions>
            </StatusModalBox>
          </ModalOverlay>

          {/* ─── MODAL REENTREGA (editar motorista + sequência) ─────────── */}
          <ModalOverlay $visible={reModal.open}>
            <StatusModalBox>
              <h3>↩ Reentregar pedido</h3>
              <p>
                NF <strong>{reModal.order?.order_number}</strong> — o pedido volta para{' '}
                <strong>Aguardando Recebimento</strong>, sai do box e é reatribuído ao
                motorista/sequência abaixo.
              </p>

              <FormLabel>Motorista</FormLabel>
              <FormSelect
                value={reModal.driverId}
                onChange={e => {
                  // React 16 pool events: capturar o valor ANTES do setter async
                  const v = e.target.value
                  setReModal(m => ({ ...m, driverId: v }))
                }}
                disabled={reModal.busy}
              >
                <option value="">Sem motorista</option>
                {drivers
                  .filter(d => d.status || d.id === reModal.driverId)
                  .map(d => (
                    <option key={d.id} value={d.id}>{d.name}</option>
                  ))}
              </FormSelect>

              <FormLabel>Sequência de entrega</FormLabel>
              <FormInput
                type="number"
                min="0"
                step="1"
                value={reModal.sequence}
                onChange={e => {
                  const v = e.target.value
                  setReModal(m => ({ ...m, sequence: v }))
                }}
                placeholder="Ex: 3"
                disabled={reModal.busy}
              />

              <ModalActions>
                <ModalBtn onClick={closeReentrega} disabled={reModal.busy}>Cancelar</ModalBtn>
                <ModalBtn $primary onClick={submitReentrega} disabled={reModal.busy}>
                  {reModal.busy ? 'Reentregando…' : '↩ Reentregar'}
                </ModalBtn>
              </ModalActions>
            </StatusModalBox>
          </ModalOverlay>

          {/* ─── CONFIRM MODAL ─────────────────────────────────────────── */}
          <ModalOverlay $visible={confirmDialog.open}>
            <ConfirmBox $tone={confirmDialog.tone}>
              <ConfirmIcon $tone={confirmDialog.tone}>
                {confirmDialog.tone === 'danger' ? '⚠' : '?'}
              </ConfirmIcon>
              <ConfirmTitle>{confirmDialog.title}</ConfirmTitle>
              <ConfirmMessage>{confirmDialog.message}</ConfirmMessage>
              <ModalActions>
                <ModalBtn onClick={() => closeConfirm(false)}>Cancelar</ModalBtn>
                <ModalBtn
                  $primary={confirmDialog.tone !== 'danger'}
                  $danger={confirmDialog.tone === 'danger'}
                  onClick={() => closeConfirm(true)}
                >
                  {confirmDialog.confirmLabel}
                </ModalBtn>
              </ModalActions>
            </ConfirmBox>
          </ModalOverlay>
        </>
      )}
    </Dashboard>
  )
}
